<template>
  <b-modal
    :visible="visible"
    centered
    hide-header
    hide-footer
    scrollable
    dialog-class="modal-color-black merge-persons-dialog"
    @hide="onDialogHide"
  >
    <confirm-merge-dialog :visible="showConfirmDialog" @cancel="onMergeCancel" @confirm="onMergeConfirm" />

    <div class="merge-persons-header">
      <h3 class="merge-persons-header-text">{{ $t('Merge contacts') }}</h3>
    </div>

    <div v-if="arePersonsValid">
      <div class="merge-persons-row">
        <div class="column-title">{{ $t('Select master (Which contact card to save info to)') }}</div>
        <div class="column-input">
          <b-form-radio v-model="selected.master" name="master" :disabled="processing" value="left">
            {{ fullName.left }}
          </b-form-radio>
          <span v-if="person.left" class="column-subtitle">{{
            person.left.lastLoginDate > '0000-01-01 00:00:00' ? $t('Log in actively') : $t('Does not log in actively')
          }}</span>
        </div>
        <div class="column-input">
          <b-form-radio v-model="selected.master" name="master" :disabled="processing" value="right">
            {{ fullName.right }}
          </b-form-radio>
          <span v-if="person.right" class="column-subtitle">{{
            person.right.lastLoginDate > '0000-01-01 00:00:00' ? $t('Log in actively') : $t('Does not log in actively')
          }}</span>
        </div>
      </div>

      <div v-if="hasPermissionsToDelete" class="merge-persons-row">
        <div class="column-title column-delete">{{ $t('Remove this after merging') }}</div>
        <div class="column-input">
          <b-form-checkbox
            :disabled="selected.master === 'left' || processing"
            :checked="selected.delete === 'left' && selected.master !== 'left'"
            @change="$event ? (selected.delete = 'left') : (selected.delete = null)"
          />
        </div>
        <div class="column-input">
          <b-form-checkbox
            :disabled="selected.master === 'right' || processing"
            :checked="selected.delete === 'right' && selected.master !== 'right'"
            @change="$event ? (selected.delete = 'right') : (selected.delete = null)"
          />
        </div>
      </div>

      <div v-for="field in fields" :key="field.key" class="merge-persons-row">
        <div class="column-title">{{ field.title }}</div>
        <div class="column-input">
          <b-form-radio v-model="selected[field.key]" :name="field.key" :disabled="processing" value="left">
            {{ getPersonFieldValue('left', field.key) }}
          </b-form-radio>
        </div>
        <div class="column-input">
          <b-form-radio v-model="selected[field.key]" :name="field.key" :disabled="processing" value="right">
            {{ getPersonFieldValue('right', field.key) }}
          </b-form-radio>
        </div>
      </div>
    </div>

    <div v-else class="merge-persons-row">{{ $t('Persons data corrupted') }}</div>

    <div class="merge-persons-actions">
      <b-spinner v-if="processing" variant="primary" />

      <b-button
        :disabled="processing"
        pill
        size="sm"
        variant="secondary"
        class="text-uppercase btn-text mr-2"
        @click="onCancelClick"
      >
        <span>{{ $t('confirm-cancel') }}</span>
      </b-button>

      <b-button
        :disabled="!arePersonsValid || processing"
        pill
        size="sm"
        variant="primary"
        class="text-uppercase mx-2"
        @click="onMergeClick"
      >
        <span>{{ $t('Merge') }}</span>
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import { EventBus } from '@/common/eventbus';

import ConfirmMergeDialog from '@/components/organisation/ConfirmMergeDialog';

export default {
  name: 'MergePersonsDialog',

  components: { ConfirmMergeDialog },

  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    leftPersonId: {
      type: String,
      default: null,
    },
    rightPersonId: {
      type: String,
      default: null,
    },
  },

  data() {
    const fields = [
      { key: 'firstName', title: this.$t('Name') },
      { key: 'lastName', title: this.$t('Last Name') },
      { key: 'phone', title: this.$t('Phone') },
      { key: 'email', title: this.$t('E-mail') },
      { key: 'id06', title: this.$t('ID-06') },
      { key: 'supervisor', title: this.$t('Nearest Manager - Name') },
      { key: 'supervisorPhone', title: this.$t('Nearest Manager - Phone Number') },
      { key: 'nextOfKin', title: this.$t('Nearest Relative - Name') },
      { key: 'nextOfKinPhone', title: this.$t('Nearest Relative - Phone Number') },
      { key: 'allergiesAndDiseases', title: this.$t('Allergies/Diseases') },
      { key: 'idFile', title: this.$t('Photo ID-06') },
    ];
    const selected = { master: 'left', delete: null };
    fields.forEach(({ key }) => {
      selected[key] = 'left';
    });

    return {
      showConfirmDialog: false,
      processing: false,
      fields,
      selected,
      idFile: {
        left: false,
        right: false,
      },
      person: {
        left: null,
        right: null,
      },
      fullName: {
        left: null,
        right: null,
      },
    };
  },

  computed: {
    ...mapGetters(['getPerson', 'hasSimplePermissions']),

    arePersonsValid() {
      return !!(this.person.left && this.person.left._id && this.person.right && this.person.right._id);
    },

    hasPermissionsToDelete() {
      return this.hasSimplePermissions('deletePerson');
    },
  },

  methods: {
    ...mapActions(['deletePerson', 'savePersonProperties', 'savePersonFile', 'getPersonFiles']),

    getPersonFieldValue(side, field) {
      if (field === 'idFile') {
        return this.idFile[side] ? this.$t('Yes') : this.$t('No');
      }

      const value = this.person[side] && this.person[side][field];
      if (Array.isArray(value)) {
        return value.length ? value.join(', ') : '-';
      }

      return value == null || value === '' ? '-' : value;
    },

    async loadPersonFiles(side, personId) {
      const doc = await this.getPersonFiles(personId);
      const files = (doc && doc.filesLinks) || [];
      this.idFile[side] = files.find(({ fileName }) => fileName === 'id06CardFile');
    },

    updatePerson(side, personId) {
      if (personId) {
        const person = this.getPerson(personId);
        this.person[side] = person;
        this.fullName[side] = person ? [person.firstName, person.lastName].filter(Boolean).join(' ') : null;
        this.loadPersonFiles(side, personId);
      } else {
        this.person[side] = null;
        this.fullName[side] = null;
        this.idFile[side] = null;
      }

      this.selected = { master: 'left', delete: null };
      this.fields.forEach(({ key }) => {
        this.selected[key] = 'left';
      });
    },

    onDialogHide(event) {
      if (this.processing) {
        event.preventDefault();
      } else {
        this.$emit('close');
      }
    },

    onMergeClick() {
      if (!this.selected.delete || this.selected.delete === this.selected.master) {
        this.showConfirmDialog = true;
      } else {
        this.merge();
      }
    },

    onMergeCancel() {
      this.showConfirmDialog = false;
    },

    onMergeConfirm() {
      this.showConfirmDialog = false;
      this.merge();
    },

    async merge() {
      this.processing = true;
      try {
        const updatePerson = this.person[this.selected.master];
        const otherPerson = this.selected.master === 'left' ? this.person.right : this.person.left;
        const properties = {};
        this.fields.forEach(({ key }) => {
          if (key !== 'idFile' && this.selected[key] !== this.selected.master) {
            properties[key] = otherPerson[key];
          }
        });

        await this.savePersonProperties({ updatePerson, newPersonProperties: properties });

        if (this.selected.idFile !== this.selected.master) {
          await this.savePersonFile({
            personId: updatePerson._id,
            name: 'id06CardFile',
            file: this.selected.master === 'left' ? this.idFile.right : this.idFile.left,
          });
        }

        const deleted =
          this.hasPermissionsToDelete && this.selected.delete && this.selected.delete !== this.selected.master;
        if (deleted) {
          await this.deletePerson({ person: otherPerson });
        }

        this.$emit('save', deleted ? otherPerson._id : null);
      } catch (error) {
        console.error(error);
        EventBus.$emit('showMessageToUser', {
          message: this.$t('Failed to merge contacts'),
          type: 'error',
          duration: 3000,
        });
      } finally {
        this.processing = false;
      }
    },

    onCancelClick() {
      if (!this.processing) {
        this.$emit('close');
      }
    },
  },

  watch: {
    leftPersonId: {
      immediate: true,
      handler(nextValue) {
        this.updatePerson('left', nextValue);
      },
    },

    rightPersonId: {
      immediate: true,
      handler(nextValue) {
        this.updatePerson('right', nextValue);
      },
    },

    'selected.master'(next, prev) {
      if (next === prev) {
        return;
      }

      this.fields.forEach(({ key }) => {
        if (key !== 'delete') {
          this.selected[key] = next;
        }
      });
    },
  },
};
</script>

<style>
.merge-persons-dialog {
  min-width: 1208px;
}

.merge-persons-dialog .modal-body {
  padding: 32px 52px;
}
</style>

<style scoped>
.merge-persons-header {
  border-bottom: 2px solid var(--bs-gray-500);
}

.merge-persons-header-text {
  font-weight: 700;
}

.merge-persons-row {
  min-height: 42px;
  padding: 8px 0;
  border-bottom: 1px solid var(--bs-gray-500);
  display: flex;
  flex-direction: row;
}

.column-title {
  flex-grow: 1;
  font-weight: 700;
}

.column-input {
  width: 30%;
  font-weight: 700;
}

.column-input >>> .custom-radio {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.column-input >>> .custom-radio .custom-control-label {
  padding-left: 12px;
  cursor: pointer;
}

.column-delete {
  color: var(--bs-danger);
}

.column-subtitle {
  color: var(--bs-gray-500);
  font-weight: 400;
  font-size: 0.875rem;
}

.merge-persons-actions {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 32px;
}
</style>



// WEBPACK FOOTER //
// src/components/organisation/MergePersonsDialog.vue